import { type ReactNode, Suspense } from 'react';
import { Navigate, type RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { RouteErrorFallback } from './RouteErrorFallback';
import { AccessLockOutlet } from './AccessLockOutlet';
import { CommunityFeatureGuard } from './CommunityFeatureGuard';
import { ProtectedRoute } from './ProtectedRoute';
import { AuthCallbackPage } from '@/pages/Login/AuthCallbackPage';
import { Links, ROUTE_ALIASES, VOLUNTEERS_TAB, VOLUNTEER_DETAILS_TAB } from '@/routing/routesAliases';
import { MESSAGE_STATUS, USER_FORM_STATUS } from '@/types';
import { lazyWithRetry } from '@/helpers/lazyWithRetry';
import { LoadingPage } from '@/pages/LoadingPage/LoadingPage';
import { LoaderOverlay } from '@/components/LoaderOverlay';
import i18next from 'i18next';

const withPageSuspense = (element: ReactNode) => <Suspense fallback={<LoaderOverlay />}>{element}</Suspense>;

const withPublicPageSuspense = (element: ReactNode) => <Suspense fallback={<LoadingPage />}>{element}</Suspense>;

const Home = lazyWithRetry(() => import('@/pages/Home').then((m) => ({ default: m.Home })));
const Groups = lazyWithRetry(() => import('@/pages/Groups/Groups').then((m) => ({ default: m.Groups })));
const GroupDetails = lazyWithRetry(() =>
  import('@/pages/GroupDetails/GroupDetails').then((m) => ({ default: m.GroupDetails }))
);
const GroupSettings = lazyWithRetry(() =>
  import('@/pages/GroupSettings/GroupSettings').then((m) => ({ default: m.GroupSettings }))
);
const CalendarPage = lazyWithRetry(() =>
  import('@/pages/Calendar/CalendarPage').then((m) => ({ default: m.CalendarPage }))
);
const ParishInfo = lazyWithRetry(() => import('@/pages/ParishInfo').then((m) => ({ default: m.ParishInfo })));
const ParishDetails = lazyWithRetry(() => import('@/pages/ParishDetails').then((m) => ({ default: m.ParishDetails })));
const AppSetup = lazyWithRetry(() => import('@/pages/AppSetup').then((m) => ({ default: m.AppSetup })));
const Members = lazyWithRetry(() => import('@/pages/Members/Members').then((m) => ({ default: m.Members })));
const Community = lazyWithRetry(() => import('@/pages/Community').then((m) => ({ default: m.Community })));
const CommunityListSettings = lazyWithRetry(() =>
  import('@/pages/CommunityListSettings/CommunityListSettings').then((m) => ({ default: m.CommunityListSettings }))
);
const Login = lazyWithRetry(() => import('@/pages/Login').then((m) => ({ default: m.Login })));
const Logout = lazyWithRetry(() => import('@/pages/Logout').then((m) => ({ default: m.Logout })));
const NotFound = lazyWithRetry(() => import('@/pages/NotFound').then((m) => ({ default: m.NotFound })));
const ErrorPage = lazyWithRetry(() => import('@/pages/ErrorPage').then((m) => ({ default: m.ErrorPage })));
const ProfileRedirect = lazyWithRetry(() =>
  import('@/pages/Profile/ProfileRedirect').then((m) => ({ default: m.ProfileRedirect }))
);

const Message = lazyWithRetry(() => import('@/pages/Message/Message').then((m) => ({ default: m.Message })));
const MessageAnnouncements = lazyWithRetry(() =>
  import('@/pages/MessageAnnouncements/MessageAnnouncements').then((m) => ({ default: m.MessageAnnouncements }))
);
const MessageDetailsPage = lazyWithRetry(() =>
  import('@/pages/MessageDetailsPage/MessageDetailsPage').then((m) => ({ default: m.MessageDetailsPage }))
);
const MessageStatsPage = lazyWithRetry(() =>
  import('@/pages/MessageStatisticsReport/MessageStatisticsReport').then((m) => ({ default: m.MessageStatsPage }))
);
const MessageTemplatesPage = lazyWithRetry(() =>
  import('@/pages/MessageTemplates/MessageTemplates').then((m) => ({ default: m.MessageTemplatesPage }))
);
const TextStatus = lazyWithRetry(() =>
  import('@/pages/TextStatus/TextStatus').then((m) => ({ default: m.TextStatus }))
);
const Messages = lazyWithRetry(() => import('@/pages/Messages/Messages').then((m) => ({ default: m.Messages })));

const UserFormCreatePage = lazyWithRetry(() =>
  import('@/pages/UserFormCreatePage/UserFormCreatePage').then((m) => ({ default: m.UserFormCreatePage }))
);
const UserFormBuilderPage = lazyWithRetry(() =>
  import('@/pages/UserFormBuilderPage/UserFormBuilderPage').then((m) => ({ default: m.UserFormBuilderPage }))
);
const UserFormPublishedSuccessPage = lazyWithRetry(() =>
  import('@/pages/UserFormPublishedSuccessPage/UserFormPublishedSuccessPage').then((m) => ({
    default: m.UserFormPublishedSuccessPage,
  }))
);
const UserFormResultsPage = lazyWithRetry(() =>
  import('@/pages/UserFormResultsPage/UserFormResultsPage').then((m) => ({ default: m.UserFormResultsPage }))
);
const UserFormsTemplatesPage = lazyWithRetry(() =>
  import('@/pages/UserFormsTemplatesPage/UserFormsTemplatesPage').then((m) => ({ default: m.UserFormsTemplatesPage }))
);
const UserFormsPage = lazyWithRetry(() =>
  import('@/pages/UserFormsPage/UserFormsPage').then((m) => ({ default: m.UserFormsPage }))
);
const MinistryHome = lazyWithRetry(() =>
  import('@/pages/Ministry/MinistryHome').then((m) => ({ default: m.MinistryHome }))
);
const MinistryGroups = lazyWithRetry(() =>
  import('@/pages/MinistryGroups/MinistryGroups').then((m) => ({ default: m.MinistryGroups }))
);
const MinistryGroupSettings = lazyWithRetry(() =>
  import('@/pages/MinistryGroupSettings/MinistryGroupSettings').then((m) => ({ default: m.MinistryGroupSettings }))
);
const MinistryGroupEmptyState = lazyWithRetry(() =>
  import('@/pages/MinistryGroupSettings/MinistryGroupSettings').then((m) => ({ default: m.MinistryGroupEmptyState }))
);
const MinistryVolunteers = lazyWithRetry(() =>
  import('@/pages/MinistryVolunteers').then((m) => ({ default: m.MinistryVolunteers }))
);
const VolunteerDetails = lazyWithRetry(() =>
  import('@/pages/VolunteerDetails').then((m) => ({ default: m.VolunteerDetails }))
);

type RouteConfig = RouteObject & {
  element: ReactNode;
  children?: RouteConfig[];
};

const publicRoutes: RouteConfig[] = [
  {
    path: ROUTE_ALIASES.AUTH_CALLBACK_PAGE,
    element: withPublicPageSuspense(<AuthCallbackPage />),
  },
  {
    path: ROUTE_ALIASES.LOGIN,
    element: withPublicPageSuspense(<Login />),
  },
  {
    path: ROUTE_ALIASES.LOGOUT,
    element: withPublicPageSuspense(<Logout />),
  },
  {
    path: ROUTE_ALIASES.ERROR,
    element: withPublicPageSuspense(<ErrorPage />),
  },
  {
    path: '*',
    element: withPublicPageSuspense(<NotFound />),
  },
];

const authenticatedRoutes: RouteConfig[] = [
  {
    path: ROUTE_ALIASES.HOME,
    element: <ProtectedRoute />,
    children: [
      {
        path: '',
        element: withPageSuspense(<Home />),
      },
      {
        path: `${ROUTE_ALIASES.GROUPS}`,
        element: withPageSuspense(<Groups />),
        children: [
          {
            path: `${ROUTE_ALIASES.GROUPS_ID}/${ROUTE_ALIASES.GROUPS_SETTINGS}`,
            element: withPageSuspense(<GroupSettings />),
          },
          {
            path: `${ROUTE_ALIASES.GROUPS_ID}`,
            element: withPageSuspense(<GroupDetails />),
          },
        ],
      },
      {
        path: ROUTE_ALIASES.CALENDAR,
        element: withPageSuspense(<CalendarPage />),
      },
      {
        path: ROUTE_ALIASES.PARISH_INFO_ROOT,
        element: withPageSuspense(<ParishInfo />),
      },
      {
        path: ROUTE_ALIASES.PARISH_DETAILS_ROOT,
        element: withPageSuspense(<ParishDetails />),
        children: [
          {
            path: ROUTE_ALIASES.PARISH_DETAILS_ID,
            element: withPageSuspense(<ParishDetails />),
          },
          {
            path: ROUTE_ALIASES.PARISH_DETAILS_INFO_TYPE,
            element: withPageSuspense(<ParishDetails />),
          },
          {
            path: ROUTE_ALIASES.PARISH_DETAILS_DIRECTORY,
            element: withPageSuspense(<ParishDetails />),
          },
          {
            path: ROUTE_ALIASES.PARISH_DETAILS,
            element: withPageSuspense(<ParishDetails />),
          },
          {
            path: ROUTE_ALIASES.PARISH_EDIT,
            element: withPageSuspense(<ParishDetails />),
          },
        ],
      },
      {
        path: ROUTE_ALIASES.APP_SETUP_ROOT,
        element: withPageSuspense(<AppSetup />),
        children: [
          {
            path: ROUTE_ALIASES.APP_SETUP,
            element: withPageSuspense(<AppSetup />),
          },
        ],
      },
      {
        path: ROUTE_ALIASES.MEMBERS,
        element: withPageSuspense(<Members />),
      },
      {
        path: ROUTE_ALIASES.COMMUNITY,
        element: (
          <CommunityFeatureGuard>
            <Navigate to={Links.getCommunityRoute()} replace />
          </CommunityFeatureGuard>
        ),
      },
      {
        path: `${ROUTE_ALIASES.COMMUNITY}/${ROUTE_ALIASES.COMMUNITY_KIND}`,
        element: <CommunityFeatureGuard>{withPageSuspense(<Community />)}</CommunityFeatureGuard>,
        children: [
          {
            path: `${ROUTE_ALIASES.COMMUNITY_LISTS}/${ROUTE_ALIASES.COMMUNITY_LIST_ID}/${ROUTE_ALIASES.COMMUNITY_LIST_SETTINGS}`,
            element: <CommunityFeatureGuard>{withPageSuspense(<CommunityListSettings />)}</CommunityFeatureGuard>,
          },
        ],
      },
      {
        path: `${ROUTE_ALIASES.COMMUNITY}/${ROUTE_ALIASES.COMMUNITY_KIND}/${ROUTE_ALIASES.COMMUNITY_LISTS}/${ROUTE_ALIASES.COMMUNITY_LIST_ID}`,
        element: <CommunityFeatureGuard>{withPageSuspense(<Community />)}</CommunityFeatureGuard>,
      },
      {
        path: ROUTE_ALIASES.PROFILE,
        element: withPageSuspense(<ProfileRedirect />),
      },
      {
        path: ROUTE_ALIASES.MESSAGES,
        element: <Navigate to={ROUTE_ALIASES.MESSAGES_INBOX} replace />,
      },
      {
        path: ROUTE_ALIASES.MESSAGES_CREATE,
        element: withPageSuspense(<Message />),
      },
      {
        path: ROUTE_ALIASES.MESSAGES_INBOX,
        element: withPageSuspense(<Messages status={MESSAGE_STATUS.INBOX} />),
        children: [
          {
            path: `${ROUTE_ALIASES.MESSAGES_INBOX}/:id`,
            element: withPageSuspense(<MessageDetailsPage />),
          },
          {
            path: `${ROUTE_ALIASES.MESSAGES_INBOX}/:id/stats`,
            element: withPageSuspense(<MessageStatsPage />),
          },
        ],
      },
      {
        path: ROUTE_ALIASES.MESSAGES_SENT,
        element: withPageSuspense(<Messages status={MESSAGE_STATUS.SENT} />),
        children: [
          {
            path: `${ROUTE_ALIASES.MESSAGES_SENT}/:id`,
            element: withPageSuspense(<MessageDetailsPage />),
          },
          {
            path: `${ROUTE_ALIASES.MESSAGES_SENT}/:id/stats`,
            element: withPageSuspense(<MessageStatsPage />),
          },
        ],
      },
      {
        path: ROUTE_ALIASES.MESSAGES_ANNOUNCEMENTS,
        element: withPageSuspense(<MessageAnnouncements />),
      },
      {
        path: ROUTE_ALIASES.MESSAGES_TEMPLATES,
        element: withPageSuspense(<MessageTemplatesPage />),
      },
      {
        path: ROUTE_ALIASES.MESSAGES_SCHEDULED,
        element: withPageSuspense(<Messages status={MESSAGE_STATUS.SCHEDULED} />),
        children: [
          {
            path: `${ROUTE_ALIASES.MESSAGES_SCHEDULED}/:id`,
            element: withPageSuspense(<MessageDetailsPage />),
          },
          {
            path: `${ROUTE_ALIASES.MESSAGES_SCHEDULED}/:id/stats`,
            element: withPageSuspense(<MessageStatsPage />),
          },
        ],
      },
      {
        path: ROUTE_ALIASES.MESSAGES_TEXT_STATUS,
        element: withPageSuspense(<TextStatus />),
      },
      {
        path: ROUTE_ALIASES.MESSAGES_DRAFTS,
        element: withPageSuspense(<Messages status={MESSAGE_STATUS.DRAFT} />),
        children: [
          {
            path: `${ROUTE_ALIASES.MESSAGES_DRAFTS}/:id`,
            element: withPageSuspense(<MessageDetailsPage />),
          },
          {
            path: `${ROUTE_ALIASES.MESSAGES_DRAFTS}/:id/stats`,
            element: withPageSuspense(<MessageStatsPage />),
          },
        ],
      },
      {
        path: ROUTE_ALIASES.MESSAGES_DELETED,
        element: withPageSuspense(<Messages status={MESSAGE_STATUS.DELETED} />),
        children: [
          {
            path: `${ROUTE_ALIASES.MESSAGES_DELETED}/:id`,
            element: withPageSuspense(<MessageDetailsPage />),
          },
          {
            path: `${ROUTE_ALIASES.MESSAGES_DELETED}/:id/stats`,
            element: withPageSuspense(<MessageStatsPage />),
          },
        ],
      },

      {
        path: ROUTE_ALIASES.FORMS_CREATE,
        element: withPageSuspense(<UserFormCreatePage />),
      },
      {
        path: ROUTE_ALIASES.FORMS_BUILDER_ID,
        element: withPageSuspense(<UserFormBuilderPage />),
      },
      {
        path: ROUTE_ALIASES.FORMS_PUBLISHED_SUCCESS_ID,
        element: withPageSuspense(<UserFormPublishedSuccessPage />),
      },
      {
        path: ROUTE_ALIASES.FORMS_RESULTS_ID,
        element: withPageSuspense(<UserFormResultsPage />),
      },
      {
        path: ROUTE_ALIASES.FORMS,
        element: <Navigate to={ROUTE_ALIASES.FORMS_PUBLISHED} replace />,
      },
      {
        path: ROUTE_ALIASES.FORMS_PUBLISHED,
        element: withPageSuspense(
          <UserFormsPage
            key={USER_FORM_STATUS.ACTIVE}
            title={i18next.t('userForm.published', { ns: 'global' })}
            status={USER_FORM_STATUS.ACTIVE}
          />
        ),
      },
      {
        path: ROUTE_ALIASES.FORMS_UNPUBLISHED,
        element: withPageSuspense(
          <UserFormsPage
            key={USER_FORM_STATUS.INACTIVE}
            title={i18next.t('userForm.unpublished', { ns: 'global' })}
            status={USER_FORM_STATUS.INACTIVE}
          />
        ),
      },
      {
        path: ROUTE_ALIASES.FORMS_TEMPLATES,
        element: withPageSuspense(<UserFormsTemplatesPage />),
      },
      {
        path: ROUTE_ALIASES.FORMS_DRAFTS,
        element: withPageSuspense(
          <UserFormsPage
            key={USER_FORM_STATUS.DRAFT}
            title={i18next.t('userForm.drafts', { ns: 'global' })}
            status={USER_FORM_STATUS.DRAFT}
          />
        ),
      },
      {
        path: ROUTE_ALIASES.MINISTRIES,
        element: withPageSuspense(<MinistryHome />),
      },
      {
        path: ROUTE_ALIASES.MINISTRIES_CALENDAR_VIEW,
        element: withPageSuspense(<MinistryHome />),
      },
      {
        path: ROUTE_ALIASES.MINISTRIES_SCHEDULING,
        element: withPageSuspense(<MinistryHome />),
      },
      {
        path: ROUTE_ALIASES.MINISTRIES_GROUP,
        element: withPageSuspense(<MinistryGroups />),
        children: [
          {
            path: ROUTE_ALIASES.MINISTRIES_GROUP_ID,
            element: withPageSuspense(<MinistryGroupSettings />),
          },
          {
            path: '',
            element: withPageSuspense(<MinistryGroupEmptyState />),
          },
        ],
      },
      {
        path: ROUTE_ALIASES.MINISTRIES_VOLUNTEERS,
        element: withPageSuspense(<MinistryVolunteers />),
        children: [
          {
            index: true,
            element: <Navigate to={VOLUNTEERS_TAB.VOLUNTEERS} replace />,
          },
          {
            path: VOLUNTEERS_TAB.VOLUNTEERS,
            element: null,
          },
          {
            path: VOLUNTEERS_TAB.FAMILIES,
            element: null,
          },
          {
            path: VOLUNTEERS_TAB.TEAMS,
            element: null,
          },
          {
            path: VOLUNTEERS_TAB.ROTATIONAL_SCHEDULE,
            element: null,
          },
        ],
      },
      {
        path: ROUTE_ALIASES.MINISTRIES_VOLUNTEER_ID,
        children: [
          {
            index: true,
            element: <Navigate to={VOLUNTEER_DETAILS_TAB.GENERAL} replace />,
          },
          {
            path: ':tab',
            element: withPageSuspense(<VolunteerDetails />),
          },
        ],
      },
      {
        path: ROUTE_ALIASES.MINISTRIES_REPORTS,
        element: withPageSuspense(<MinistryHome />),
      },
      {
        path: ROUTE_ALIASES.MINISTRIES_SETTINGS,
        element: withPageSuspense(<MinistryHome />),
      },
    ],
  },
];

const routesWithAccessLock: RouteConfig[] = [
  {
    element: <AccessLockOutlet />,
    errorElement: <RouteErrorFallback />,
    children: [...publicRoutes, ...authenticatedRoutes],
  },
];

const router = createBrowserRouter(routesWithAccessLock);

export const Routes = () => {
  return <RouterProvider router={router} />;
};
